/**
 * Custom hook pour la gestion des avoirs (credit notes)
 * ✅ SKILL FACTURXPRO: Architecture 3 couches
 *
 * Component (CreditNotesManagement.tsx)
 *    ↓
 * Custom Hook (useCreditNotesManagement) ← VOUS ÊTES ICI
 *    ↓
 * API Layer (creditNotesAPI)
 *    ↓
 * Backend
 */

import { useState, useEffect, useCallback } from 'react';
import { CreditNote, creditNotesAPI, CreditNoteStatus, CreateCreditNoteDTO, UpdateCreditNoteDTO } from '../utils/creditNotesAPI';
import { notify } from '../stores/notificationStore';
import { logger } from '../utils/logger';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface UseCreditNotesManagementReturn {
  // Data
  creditNotes: CreditNote[];
  pagination: Pagination;
  stats: {
    totalCount: number;
    totalAmount: number;
    byStatus: Record<CreditNoteStatus, { count: number; amount: number }>;
  } | null;

  // UI State
  loading: boolean;
  showForm: boolean;
  showDetails: boolean;
  editingCreditNote: CreditNote | null;
  selectedCreditNote: CreditNote | null;

  // Filter State
  searchTerm: string;
  statusFilter: CreditNoteStatus | 'ALL';
  invoiceIdFilter: string;

  // Actions - CRUD
  fetchCreditNotes: () => Promise<void>;
  fetchStats: () => Promise<void>;
  createCreditNote: (data: CreateCreditNoteDTO) => Promise<void>;
  updateCreditNote: (id: string, data: UpdateCreditNoteDTO) => Promise<void>;
  deleteCreditNote: (id: string) => Promise<void>;
  updateStatus: (id: string, status: CreditNoteStatus) => Promise<void>;
  applyCreditNote: (id: string) => Promise<void>;

  // Actions - UI
  setShowForm: (show: boolean) => void;
  setShowDetails: (show: boolean) => void;
  setEditingCreditNote: (creditNote: CreditNote | null) => void;
  setSelectedCreditNote: (creditNote: CreditNote | null) => void;
  setSearchTerm: (term: string) => void;
  setStatusFilter: (status: CreditNoteStatus | 'ALL') => void;
  setInvoiceIdFilter: (invoiceId: string) => void;
  setPagination: (pagination: Pagination | ((prev: Pagination) => Pagination)) => void;

  // Computed
  handleSearch: (term: string) => void;
  goToPage: (page: number) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: string) => string;
  getStatusBadgeClass: (status: CreditNoteStatus) => string;
  getStatusLabel: (status: CreditNoteStatus) => string;
}

export function useCreditNotesManagement(): UseCreditNotesManagementReturn {
  // ==================== STATE ====================

  // Data state
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [stats, setStats] = useState<{
    totalCount: number;
    totalAmount: number;
    byStatus: Record<CreditNoteStatus, { count: number; amount: number }>;
  } | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingCreditNote, setEditingCreditNote] = useState<CreditNote | null>(null);
  const [selectedCreditNote, setSelectedCreditNote] = useState<CreditNote | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CreditNoteStatus | 'ALL'>('ALL');
  const [invoiceIdFilter, setInvoiceIdFilter] = useState('');

  // ==================== DATA LOADING ====================

  /**
   * Charger les avoirs
   */
  const fetchCreditNotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await creditNotesAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        invoiceId: invoiceIdFilter || undefined,
      });

      setCreditNotes(response.creditNotes);
      setPagination(response.pagination);
    } catch (error) {
      logger.error('fetchCreditNotes error:', error);
      notify.error('Erreur lors du chargement des avoirs', 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, invoiceIdFilter]);

  /**
   * Charger les statistiques
   */
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await creditNotesAPI.getStats();
      setStats(statsData);
    } catch (error) {
      logger.error('fetchStats error:', error);
      // Silent fail - stats are optional
    }
  }, []);

  // ==================== CRUD OPERATIONS ====================

  /**
   * Créer un avoir
   */
  const createCreditNote = useCallback(async (data: CreateCreditNoteDTO) => {
    try {
      setLoading(true);
      await creditNotesAPI.create(data);
      notify.success('Avoir créé avec succès', 'Succès');
      await fetchCreditNotes();
      await fetchStats();
      setShowForm(false);
    } catch (error) {
      logger.error('createCreditNote error:', error);
      notify.error('Erreur lors de la création de l\'avoir', 'Erreur');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchCreditNotes, fetchStats]);

  /**
   * Mettre à jour un avoir
   */
  const updateCreditNote = useCallback(async (id: string, data: UpdateCreditNoteDTO) => {
    try {
      setLoading(true);
      await creditNotesAPI.update(id, data);
      notify.success('Avoir mis à jour avec succès', 'Succès');
      await fetchCreditNotes();
      setShowForm(false);
      setEditingCreditNote(null);
    } catch (error) {
      logger.error('updateCreditNote error:', error);
      notify.error('Erreur lors de la mise à jour de l\'avoir', 'Erreur');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchCreditNotes]);

  /**
   * Supprimer un avoir
   */
  const deleteCreditNote = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await creditNotesAPI.delete(id);
      notify.success('Avoir supprimé avec succès', 'Succès');
      await fetchCreditNotes();
      await fetchStats();
    } catch (error) {
      logger.error('deleteCreditNote error:', error);
      notify.error('Erreur lors de la suppression de l\'avoir', 'Erreur');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchCreditNotes, fetchStats]);

  /**
   * Mettre à jour le statut d'un avoir
   */
  const updateStatus = useCallback(async (id: string, status: CreditNoteStatus) => {
    try {
      setLoading(true);
      await creditNotesAPI.updateStatus(id, status);
      notify.success('Statut de l\'avoir modifié', 'Succès');
      await fetchCreditNotes();
      await fetchStats();
    } catch (error) {
      logger.error('updateStatus error:', error);
      notify.error('Erreur lors de la modification du statut', 'Erreur');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchCreditNotes, fetchStats]);

  /**
   * Appliquer un avoir
   */
  const applyCreditNote = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await creditNotesAPI.apply(id);
      notify.success('Avoir appliqué avec succès', 'Succès');
      await fetchCreditNotes();
      await fetchStats();
    } catch (error) {
      logger.error('applyCreditNote error:', error);
      notify.error('Erreur lors de l\'application de l\'avoir', 'Erreur');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchCreditNotes, fetchStats]);

  // ==================== HELPER FUNCTIONS ====================

  /**
   * Gérer la recherche
   */
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  /**
   * Aller à une page
   */
  const goToPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  /**
   * Formater une devise
   */
  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  }, []);

  /**
   * Formater une date
   */
  const formatDate = useCallback((date: string): string => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, []);

  /**
   * Obtenir la classe CSS du badge de statut
   */
  const getStatusBadgeClass = useCallback((status: CreditNoteStatus): string => {
    const baseClasses = 'px-3 py-1 rounded-full text-xs font-semibold';

    switch (status) {
      case 'DRAFT':
        return `${baseClasses} bg-gray-100 text-gray-700`;
      case 'SENT':
        return `${baseClasses} bg-blue-100 text-blue-700`;
      case 'APPLIED':
        return `${baseClasses} bg-green-100 text-green-700`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-700`;
    }
  }, []);

  /**
   * Obtenir le label du statut
   */
  const getStatusLabel = useCallback((status: CreditNoteStatus): string => {
    switch (status) {
      case 'DRAFT':
        return 'Brouillon';
      case 'SENT':
        return 'Envoyé';
      case 'APPLIED':
        return 'Appliqué';
      default:
        return status;
    }
  }, []);

  // ==================== EFFECTS ====================

  // Charger les avoirs quand la page ou les filtres changent
  useEffect(() => {
    fetchCreditNotes();
  }, [fetchCreditNotes]);

  // Charger les statistiques au montage
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ==================== RETURN ====================

  return {
    // Data
    creditNotes,
    pagination,
    stats,

    // UI State
    loading,
    showForm,
    showDetails,
    editingCreditNote,
    selectedCreditNote,

    // Filter State
    searchTerm,
    statusFilter,
    invoiceIdFilter,

    // Actions - CRUD
    fetchCreditNotes,
    fetchStats,
    createCreditNote,
    updateCreditNote,
    deleteCreditNote,
    updateStatus,
    applyCreditNote,

    // Actions - UI
    setShowForm,
    setShowDetails,
    setEditingCreditNote,
    setSelectedCreditNote,
    setSearchTerm,
    setStatusFilter,
    setInvoiceIdFilter,
    setPagination,

    // Computed
    handleSearch,
    goToPage,
    formatCurrency,
    formatDate,
    getStatusBadgeClass,
    getStatusLabel,
  };
}
